import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import Modal from '../components/Modal'
import axios from 'axios'
import { config } from '../config/environment'
import { HOME_ROUTE } from '../config/routes'
import { useToast } from '../design/Toast'
import './LandingPage.css'

const LandingPage: React.FC = () => {
  const navigate = useNavigate()
  const toast = useToast()
  const { isAuthenticated, isLoading } = useAuth()
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    inquiry_type: '',
    message: ''
  })
  
  // Modal state
  const [showModal, setShowModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate(HOME_ROUTE, { replace: true })
    }
  }, [isAuthenticated, isLoading, navigate])

  const scrollToContact = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    e.preventDefault()
    const contactSection = document.getElementById('contact')
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setShowModal(true)
  }

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true)
    
    try {
      const response = await axios.post(`${config.apiBaseUrl}/api/v1/contact/submit`, formData)
      
      if (response.data.success) {
        setShowModal(false)
        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          inquiry_type: '',
          message: ''
        })
        // Show success message
        toast.success('お問い合わせを送信しました。確認メールをご確認ください。')
      }
    } catch (error) {
      console.error('Contact form submission error:', error)
      toast.error('送信中にエラーが発生しました。もう一度お試しください。')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f8fafc'
      }}>
        <div style={{
          padding: '2rem',
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          fontSize: '1.125rem',
          color: '#6b7280'
        }}>
          Loading...
        </div>
      </div>
    )
  }

  return (
    <div className="landing-page">
      <nav className="landing-nav">
        <div className="nav-container">
          <div className="logo">
            <h2>Spread Agent</h2>
          </div>
          <div className="nav-links">
            <Link to="/login" className="nav-link">ログイン</Link>
            <Link to="/signup" className="cta-button">登録</Link>
          </div>
        </div>
      </nav>

      <section className="hero">
        <div className="container">
          <div className="hero-grid">
            <div className="hero-content">
              <h1 className="hero-title">
                AIが自動で営業リードを<br/>
                発掘し、メールを送信
              </h1>
              <p className="hero-subtitle">
                Spread Agentは、AIエージェントが潜在顧客を自動発見し、
                パーソナライズされた営業メールを生成・送信する
                次世代の営業自動化プラットフォームです
              </p>
              <div className="hero-cta">
                <Link to="/signup" className="primary-button">
                  今すぐ登録
                </Link>
                <Link to="/login" className="secondary-button">
                  デモを見る
                </Link>
              </div>
            </div>
            <div className="hero-image">
              <div className="dashboard-preview">
                <div className="preview-header">
                  <div className="preview-dots">
                    <span className="dot red"></span>
                    <span className="dot yellow"></span>
                    <span className="dot green"></span>
                  </div>
                </div>
                <div className="preview-content">
                  <div className="metric-card">
                    <h4>本日の送信メール</h4>
                    <p className="metric-number">247</p>
                  </div>
                  <div className="metric-card">
                    <h4>発見したリード</h4>
                    <p className="metric-number">1,832</p>
                  </div>
                  <div className="metric-card">
                    <h4>開封率</h4>
                    <p className="metric-number">7.2%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="features">
        <div className="container">
          <h2 className="section-title">主な機能</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🔍</div>
              <h3>AIリード発掘</h3>
              <p>
                ウェブ全体から潜在顧客を自動で発見。
                業界、企業規模、ニーズに基づいて
                最適なターゲットを特定します。
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">✉️</div>
              <h3>スマートメール生成</h3>
              <p>
                各リードに合わせたパーソナライズメールを
                AIが自動生成。開封率と返信率を
                大幅に向上させます。
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🚀</div>
              <h3>自動送信＆追跡</h3>
              <p>
                最適なタイミングでメールを自動送信。
                開封率、クリック率、返信率を
                リアルタイムで追跡します。
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>詳細な分析</h3>
              <p>
                キャンペーンのパフォーマンスを
                可視化。データに基づいた
                営業戦略の最適化が可能です。
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🤖</div>
              <h3>マルチエージェント</h3>
              <p>
                複数の専門AIエージェントが協調して
                リード発掘からメール送信まで
                全プロセスを自動化します。
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔐</div>
              <h3>セキュアな環境</h3>
              <p>
                エンタープライズグレードの
                セキュリティで顧客データを保護。
                GDPRにも完全準拠しています。
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="how-it-works">
        <div className="container">
          <h2 className="section-title">使い方</h2>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>プロダクト情報を登録</h3>
              <p>あなたの製品やサービスの情報を入力します</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>ターゲット設定</h3>
              <p>理想的な顧客プロファイルを定義します</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>AIが自動実行</h3>
              <p>リード発掘からメール送信まで全て自動化</p>
            </div>
            <div className="step">
              <div className="step-number">4</div>
              <h3>成果を確認</h3>
              <p>ダッシュボードで結果を分析・最適化</p>
            </div>
          </div>
        </div>
      </section>

      <section className="contact" id="contact">
        <div className="container">
          <h2 className="section-title">お問い合わせ</h2>
          <div className="contact-content">
            <div className="contact-info">
              <h3>サービスについてお気軽にお問い合わせください</h3>
              <p>Spread Agentの詳細や導入についてのご相談、デモのご希望など、お気軽にお問い合わせください。専門スタッフが丁寧にサポートいたします。</p>
            </div>
            <div className="contact-form">
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <input 
                    type="text" 
                    name="name"
                    placeholder="お名前" 
                    value={formData.name}
                    onChange={handleInputChange}
                    required 
                  />
                </div>
                <div className="form-group">
                  <input 
                    type="email" 
                    name="email"
                    placeholder="メールアドレス" 
                    value={formData.email}
                    onChange={handleInputChange}
                    required 
                  />
                </div>
                <div className="form-group">
                  <input 
                    type="text" 
                    name="company"
                    placeholder="会社名" 
                    value={formData.company}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <select 
                    name="inquiry_type"
                    value={formData.inquiry_type}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">お問い合わせ内容</option>
                    <option value="demo">デモのご希望</option>
                    <option value="pricing">料金について</option>
                    <option value="implementation">導入について</option>
                    <option value="support">サポートについて</option>
                    <option value="other">その他</option>
                  </select>
                </div>
                <div className="form-group">
                  <textarea 
                    name="message"
                    placeholder="メッセージをご記入ください" 
                    rows={5} 
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                  ></textarea>
                </div>
                <button type="submit" className="contact-button">
                  送信する
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="cta">
        <div className="container">
          <h2>営業を次のレベルへ</h2>
          <p>
            今すぐSpread Agentで営業プロセスを自動化し、
            売上を加速させましょう
          </p>
          <Link to="/signup" className="cta-button large">
            今すぐ始める
          </Link>
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h4>Spread Agent</h4>
              <p>AIで営業を自動化</p>
            </div>
            <div className="footer-section">
              <h4>プロダクト</h4>
              <ul>
                <li><a href="#features">機能</a></li>
                <li><a href="#contact" onClick={scrollToContact}>問い合わせ</a></li>
                <li><a href="#how-it-works">使い方</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>サポート</h4>
              <ul>
                <li><button type="button" className="footer-link" onClick={scrollToContact}>お問い合わせ</button></li>
                <li><Link to="/company-profile" className="footer-link">会社概要</Link></li>
                <li><Link to="/terms-of-service" className="footer-link">利用規約</Link></li>
                <li><Link to="/privacy-policy" className="footer-link">プライバシーポリシー</Link></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 Spread Agent. All rights reserved.</p>
          </div>
        </div>
      </footer>
      
      <Modal 
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleConfirmSubmit}
        title="お問い合わせの確認"
        message={`以下の内容でお問い合わせを送信します。よろしいですか？\n\nお名前: ${formData.name}\nメールアドレス: ${formData.email}\n会社名: ${formData.company || '未記入'}\nお問い合わせ種別: ${formData.inquiry_type}`}
        confirmText="送信"
        cancelText="キャンセル"
        isLoading={isSubmitting}
      />
    </div>
  )
}

export default LandingPage
